'use client';
import {useEffect} from 'react';
import {useRouter} from 'next/navigation';
import Cookies from 'js-cookie';
import {areSettingsAvailable} from '@/services/internal/settings/client';
import {getCurrentGameCookieName} from '@/utils/constant';

// Cap the settings-sync refreshes PER game id. router.refresh() re-runs the server render to
// pick up freshly-hydrated settings, and normally settings are available after one pass. But
// when they can NEVER surface on this render — e.g. the shell game (19369) reached by a
// logged-OUT root guest, whose getShellSdkSettings degrades to null so ShellSettingsHydrator
// can't seed the settings cookie — an unbounded refresh spins forever and crashes the tab
// (the reported infinite /register reload). A module-scoped counter (NOT a component ref)
// is what bounds it: router.refresh() is a soft nav that keeps the JS module alive but can
// re-mount the client tree, which would reset a ref every iteration. A few retries still
// cover the legit race where a refresh DOES surface settings; after that we stop and render
// degraded instead of looping the tab to death.
const MAX_SETTINGS_REFRESHES = 3;
const refreshCounts = new Map<string, number>();

export default function useSyncSettings(gid?: string) {
  const router = useRouter();
  const synced = areSettingsAvailable(gid);

  useEffect(() => {
    if (!gid) return;
    if (synced) {
      // Recovered — clear the budget so a later genuine desync can retry.
      refreshCounts.delete(gid);
      return;
    }
    const count = refreshCounts.get(gid) ?? 0;
    if (count >= MAX_SETTINGS_REFRESHES) return;
    refreshCounts.set(gid, count + 1);
    Cookies.remove(getCurrentGameCookieName(gid));
    router.refresh();
  }, [router, synced, gid]);

  return synced;
}
